import clsx from 'clsx'
import { inject, readonly, ref, provide, reactive, toRefs, type UnwrapNestedRefs, computed, onUnmounted, watch } from 'vue'

import { handleCloseWithTransitionend } from '@/utils/modal'

export type BaseModalTransition = 'top' | 'bottom' | 'scale' | 'none'
export type BaseModalPosition = 'top' | 'bottom' | 'center' | 'right' | 'left'

interface Props {
  transition?: BaseModalTransition
  position?: BaseModalPosition
  closeOutsideClickEnabled?: boolean
}

interface State {
  transformClass: string
  transformStyle: string
  overlayClass: string
  isRevealed: boolean
}

export interface UseModalRevealResult<T> {
  data?: T
  isCanceled: boolean
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useBaseModalLocal<T = any> (key: symbol, props: UnwrapNestedRefs<Props>) {
  const activatorEml = ref<HTMLElement | null>(null)
  const contenElm = ref<HTMLDivElement | null>(null)
  const state = reactive<State>({
    transformClass: '',
    transformStyle: '',
    overlayClass: clsx('opacity-65'),
    isRevealed: false
  })
  const transition = computed((): string => props.transition ?? 'scale')
  const position = computed((): string => props.position ?? 'center')
  const closeOutsideClickEnabled = computed((): boolean => props.closeOutsideClickEnabled ?? false)

  let _resolve: ((value: UseModalRevealResult<T>) => void) | undefined

  // #region Actions

  const getTransformStyle = (scale: number): string => {
    if (contenElm.value === null) {
      return ''
    }

    if (transition.value === 'bottom') {
      return `transform: translateY(${window.innerHeight - contenElm.value.offsetTop}px);`
    }

    return transition.value === 'top'
      ? `transform: translateY(-${contenElm.value.offsetTop + contenElm.value.offsetHeight}px);`
      : `transform: scale(${scale});opacity: 0.3;`
  }

  const reveal = async (): Promise<UseModalRevealResult<T>> => {
    state.isRevealed = true
    state.overlayClass = clsx('opacity-65')
    return await new Promise<UseModalRevealResult<T>>((resolve) => {
      _resolve = resolve
    })
  }

  const _close = (callback?: () => void): void => {
    if (contenElm.value === null) {
      state.isRevealed = false
      return
    }

    if (transition.value === 'none') {
      state.isRevealed = false
      callback?.()
      return
    }

    state.transformClass = clsx(['transition-transform duration-300'])
    state.transformStyle = getTransformStyle(0)
    state.overlayClass = 'opacity-0'

    handleCloseWithTransitionend(contenElm.value, () => {
      state.isRevealed = false
      callback?.()
    })
  }

  const cancel = (): void => {
    _close(() => {
      _resolve?.({ isCanceled: true })
    })
  }

  const confirm = (data?: T): void => {
    _close(() => {
      _resolve?.({ data, isCanceled: false })
    })
  }

  const playRevealAnimation = (): void => {
    if (contenElm.value === null || transition.value === 'none') {
      return
    }

    state.transformStyle = getTransformStyle(0.95)
    setTimeout(() => {
      state.transformClass = clsx('transition-all duration-300')
      state.transformStyle = 'transform: translateY(0px), scale(1);opacity: 1.0;'
    }, 0)
  }

  const handleCheckClickOutside = (e: Event): void => {
    const target = e.target as (HTMLElement | null)

    if (((contenElm.value?.contains(target)) === true) || ((activatorEml.value?.contains(target)) === true)) {
      return
    }

    cancel()
  }

  // #endregion

  // #region Watchers

  watch(() => state.isRevealed, () => {
    if (!closeOutsideClickEnabled.value) {
      return
    }

    window.removeEventListener('click', handleCheckClickOutside)

    if (state.isRevealed) {
      window.addEventListener('click', handleCheckClickOutside, true)
    }
  })

  // #endregion

  onUnmounted(() => {
    window.removeEventListener('click', handleCheckClickOutside)
  })

  return {
    key,
    activatorEml,
    contenElm,
    transition,
    position,
    ...toRefs(readonly(state)),
    reveal,
    cancel,
    confirm,
    playRevealAnimation
  }
}

const getDefaultProps = (arg1?: symbol | UnwrapNestedRefs<Props>, arg2?: UnwrapNestedRefs<Props>): UnwrapNestedRefs<Props> => {
  if (typeof arg1 === 'symbol') {
    return arg2 ?? {}
  }

  return arg1 ?? {}
}

export default function useBaseModal<T> (arg1?: symbol | UnwrapNestedRefs<Props>, arg2?: UnwrapNestedRefs<Props>): ReturnType<typeof useBaseModalLocal<T>> {
  const key = typeof arg1 === 'symbol' ? arg1 : Symbol('base-modal')
  const result = inject<ReturnType<typeof useBaseModalLocal<T>> | undefined>(key, undefined)

  if (result === undefined) {
    const props = getDefaultProps(arg1, arg2)
    const newResult = useBaseModalLocal<T>(key, props)
    provide(key, newResult)
    return newResult
  }

  return result
}
